import React, { useState, useEffect } from "react";
import { Calendar, Download, ChevronDown, RefreshCw, Package, ShoppingCart, Trash2 } from "lucide-react";
import DataTable from "react-data-table-component";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { adminServices } from "../../services/AdminServices";
import { logExport } from "../../services/ExportLogService";

const InventoryMovement = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [facility, setFacility] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [merchant, setMerchant] = useState("");
  const [merchants, setMerchants] = useState([{ value: "", label: "All Merchants" }]);
  const [dashboardData, setDashboardData] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setDashboardLoading(true);
      try {
        const params = new URLSearchParams();
        if (facility) {
          params.append("facilityId", facility);
        }
        if (merchant) {
          params.append("merchantId", merchant);
        }
        const qs = params.toString() ? `?${params.toString()}` : "";

        const [dashboardRes, metricsRes] = await Promise.all([
          adminServices.getData(`InventoryMovement/dashboard${qs}`),
          adminServices.getData(`InventoryMovement/metrics${qs}`)
        ]);
        setDashboardData(dashboardRes?.data ?? null);
        setMetricsData(metricsRes?.data ?? null);
      } catch (error) {
        console.error("Error fetching inventory data:", error);
        setDashboardData(null);
        setMetricsData(null);
      } finally {
        setDashboardLoading(false);
      }
    };
    fetchData();
  }, [facility, merchant]);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await adminServices.postData("sp/v2/getallfacilities", {});
        const data = response?.data;
        if (Array.isArray(data)) {
          const options = [{ value: "", label: "Facility" }];
          data.forEach((fac) => {
            const id = fac.facilityId;
            const name = fac.facilityName;
            if (id && name) {
              options.push({
                value: String(id),
                label: `${id} - ${name}`
              });
            }
          });
          setFacilities(options);
        }
      } catch (error) {
        console.error("Error fetching facilities:", error);
        setFacilities([{ value: "", label: "Facility" }]);
      }
    };
    fetchFacilities();
  }, []);

  useEffect(() => {
    const fetchMerchants = async () => {
      if (!facility) {
        setMerchants([{ value: "", label: "All Merchants" }]);
        setMerchant("");
        return;
      }
      try {
        const response = await adminServices.postData("sp/v2/getMerchantByFacility", {
          facilityId: facility
        });
        const data = response?.data;
        if (Array.isArray(data)) {
          const options = [{ value: "", label: "All Merchants" }];
          data.forEach((m) => {
            // Sample payload fields:
            // { id, merchant_name, merchantCode, ... }
            const id = m.merchantId ?? m.id;
            const code = m.merchantCode ?? m.code ?? "";
            const name = m.merchantName ?? m.merchant_name ?? m.name ?? "";
            if (id && name) {
              options.push({
                value: String(id),
                label: code ? `${code} - ${name}` : `${id} - ${name}`
              });
            }
          });
          setMerchants(options);
          setMerchant("");
        }
      } catch (error) {
        console.error("Error fetching merchants by facility:", error);
        setMerchants([{ value: "", label: "All Merchants" }]);
        setMerchant("");
      }
    };
    fetchMerchants();
  }, [facility]);

  useEffect(() => {
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const res = await adminServices.getInventoryMovementHistory({
          productId: undefined,
          facilityId: facility || undefined,
          merchantId: merchant || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          page: 1,
          limit: 50
        });
        const raw =
          (Array.isArray(res?.data?.history) && res.data.history) ||
          (Array.isArray(res?.data?.data) && res.data.data) ||
          (Array.isArray(res?.data) && res.data) ||
          [];
        const mapped = raw.map((row, index) => ({
          id: row.id ?? index + 1,
          timestamp: row.timestamp ?? row.dateTime ?? row.date ?? "-",
          sku: row.sku ?? row.productSku ?? "-",
          itemName: row.name ?? row.productName ?? "-",
          category: row.category ?? row.categoryName ?? "-",
          uom: row.uom ?? row.unit ?? "-",
          beginningInventory: row.beginningInventory != null ? Number(row.beginningInventory) : "-",
          deliveredQty: row.deliveredQty != null ? Number(row.deliveredQty) : "-",
          qtySold: row.qtySold != null ? Number(row.qtySold) : "-",
          remainingQty: row.remainingQty != null ? Number(row.remainingQty) : "-"
        }));
        setHistoryRows(mapped);
      } catch (error) {
        console.error("Error fetching inventory movement history:", error);
        setHistoryRows([]);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [dateFrom, dateTo, facility, merchant]);

  const dashboard = dashboardData?.dashboard ?? null;
  const m = metricsData?.metrics ?? null;

  // Key metrics - Merchant Inventory Movement (from API metrics response)
  const formatCurrency = (v) => (v != null && !isNaN(Number(v)) ? `PHP${Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null);
  const inventoryValue = formatCurrency(m?.inventoryValue);
  const itemsAvailable = m?.numberOfItemsAvailable != null ? String(m.numberOfItemsAvailable) : null;
  const lowStocks = m?.numberOfLowStockItems != null ? String(m.numberOfLowStockItems) : null;
  const outOfStock = m?.numberOfOutStockItems != null ? String(m.numberOfOutStockItems) : null;

  const stocksAvailableFallback = dashboard?.inventoryMovement?.reduce((sum, r) => sum + (Number(r.remainingQty) || 0), 0) ?? 0;
  const metrics = [
    { label: "Inventory Value", value: inventoryValue ?? "PHP0.00", icon: RefreshCw, color: "#10b981" },
    { label: "Items Available", value: itemsAvailable ?? stocksAvailableFallback.toLocaleString(), icon: Package, color: "#3b82f6" },
    { label: "Low Stocks", value: lowStocks ?? "0", icon: ShoppingCart, color: "#f59e0b" },
    { label: "Out of Stock", value: outOfStock ?? "0", icon: Trash2, color: "#ef4444" }
  ];

  // Bar chart data from API dashboard.graph - map to ensure numeric cost/revenue
  const defaultBarData = [
    { month: "January", cost: 0, revenue: 0 },
    { month: "February", cost: 0, revenue: 0 },
    { month: "March", cost: 0, revenue: 0 },
    { month: "April", cost: 0, revenue: 0 }
  ];
  const barData = (dashboard?.graph && dashboard.graph.length > 0)
    ? dashboard.graph.map((row) => ({
        month: row.month ?? "",
        cost: Number(row.cost) || 0,
        revenue: Number(row.revenue) || 0
      }))
    : defaultBarData;

  // Table data: prefer /InventoryMovement/history, fallback to dashboard.inventoryMovement
  const dashboardTableData = (dashboard?.inventoryMovement ?? []).map((row, index) => ({
    id: index + 1,
    timestamp: row.timestamp ?? "-",
    sku: row.sku ?? "-",
    itemName: row.name ?? "-",
    category: row.category ?? "-",
    uom: row.uom ?? "-",
    beginningInventory: row.beginningInventory != null ? Number(row.beginningInventory) : "-",
    deliveredQty: row.deliveredQty != null ? Number(row.deliveredQty) : "-",
    qtySold: row.qtySold != null ? Number(row.qtySold) : "-",
    remainingQty: row.remainingQty != null ? Number(row.remainingQty) : "-"
  }));

  const tableData = historyRows.length > 0 ? historyRows : dashboardTableData;

  const handleExportCsv = async () => {
    if (!tableData || tableData.length === 0) {
      window.alert("No inventory movement data to export.");
      return;
    }

    // Log the export
    await logExport('Inventory Movement');

    const headers = [
      "Timestamp",
      "SKU",
      "Item Name",
      "Category",
      "UOM",
      "Beginning Inventory",
      "Delivered QTY",
      "QTY Sold",
      "Remaining QTY"
    ];

    const rows = tableData.map((item) => [
      item.timestamp ?? "",
      item.sku ?? "",
      item.itemName ?? "",
      item.category ?? "",
      item.uom ?? "",
      item.beginningInventory ?? "",
      item.deliveredQty ?? "",
      item.qtySold ?? "",
      item.remainingQty ?? ""
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "InventoryMovement.csv");
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns = [
    { name: "Timestamp", selector: row => row.timestamp, sortable: true, minWidth: "170px" },
    { name: "SKU", selector: row => row.sku, sortable: true, minWidth: "110px", maxWidth: "140px" },
    { name: "Item Name", selector: row => row.itemName, sortable: true, minWidth: "220px", wrap: true },
    { name: "Category", selector: row => row.category, sortable: true, minWidth: "140px", wrap: true },
    { name: "UOM", selector: row => row.uom, sortable: true, minWidth: "90px", maxWidth: "110px" },
    { name: "Beginning Inventory", selector: row => row.beginningInventory, sortable: true, minWidth: "170px", right: true },
    { name: "Delivered QTY", selector: row => row.deliveredQty, sortable: true, minWidth: "140px", right: true },
    { name: "QTY Sold", selector: row => row.qtySold, sortable: true, minWidth: "120px", right: true },
    { name: "Remaining QTY", selector: row => row.remainingQty, sortable: true, minWidth: "160px", right: true }
  ];

  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#0F2A40",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "left"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    }
  };

  if (dashboardLoading) {
    return (
      <div className="dashboard-content">
        <div className="inventory-movement-page" style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
          Loading dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="inventory-movement-page">
        {/* Merchant Inventory Movement - Section with Facility dropdown (same UI as Dashboard) */}
        <div className="inventory-movement-metrics">
          <div
            className="inventory-movement-section-header"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2 className="inventory-movement-section-title">Merchant Inventory Movement</h2>
              {/* Facility dropdown - dark button style (left, beside label) */}
              <div
                className="inventory-movement-facility-dropdown"
                style={{
                  backgroundColor: "#111827",
                  borderRadius: 8,
                  padding: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                <select
                  className="inventory-movement-facility-select"
                  value={facility}
                  onChange={(e) => setFacility(e.target.value)}
                  style={{
                    backgroundColor: "transparent",
                    color: "#ffffff",
                    border: "none",
                    padding: "6px 32px 6px 12px",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    outline: "none",
                    minWidth: 140,
                    cursor: "pointer"
                  }}
                >
                  {facilities.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="inventory-movement-facility-icon"
                  size={18}
                  style={{ color: "#ffffff" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {/* Merchant dropdown - white background, black label */}
              <div
                className="inventory-movement-merchant-dropdown"
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  padding: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                <select
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                  style={{
                    backgroundColor: "transparent",
                    color: "#111827",
                    border: "none",
                    padding: "6px 32px 6px 12px",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    outline: "none",
                    minWidth: 150,
                    cursor: "pointer"
                  }}
                >
                  {merchants.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} style={{ color: "#111827" }} />
              </div>

            </div>
          </div>
          <div className="inventory-movement-metrics-grid">
            {metrics.map((metric, index) => {
              const IconComponent = metric.icon;
              return (
                <div key={index} className="inventory-movement-metric-card">
                  <div className="inventory-movement-metric-content">
                    <div className="inventory-movement-metric-info">
                      <span className="inventory-movement-metric-label">{metric.label}</span>
                      <span className="inventory-movement-metric-value">{metric.value}</span>
                    </div>
                    <div
                      className="inventory-movement-metric-icon"
                      style={{ backgroundColor: `${metric.color}20`, color: metric.color }}
                    >
                      <IconComponent size={20} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="inventory-movement-date-filters">
            <div className="inventory-movement-date-filter">
              <Calendar size={18} className="inventory-movement-date-icon" />
              <input
                type="date"
                className="inventory-movement-date-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
              />
            </div>
            <div className="inventory-movement-date-filter">
              <Calendar size={18} className="inventory-movement-date-icon" />
              <input
                type="date"
                className="inventory-movement-date-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
              />
            </div>
          </div>
        </div>

        {/* Cost and Revenue Chart */}
        <div className="inventory-movement-chart-card inventory-movement-chart-full">
          <h3 className="inventory-movement-chart-title">Cost and Revenue</h3>
          <div className="inventory-movement-chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="cost" fill="#9ca3af" name="Cost" />
                <Bar dataKey="revenue" fill="#22c55e" name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table Section */}
        <div className="inventory-movement-table-section">
          <div className="inventory-movement-table-header">
            <h2 className="inventory-movement-section-title">Inventory Movement</h2>
            <div className="inventory-movement-export-buttons">
              {/* <button className="inventory-movement-export-pdf-btn">
                <Download size={18} />
                Export PDF
              </button> */}
              <button
                className="inventory-movement-export-csv-btn"
                type="button"
                onClick={handleExportCsv}
              >
                <Download size={18} />
                Export CSV
              </button>
            </div>
          </div>
          <div
            className="inventory-movement-table-container"
            style={{
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              backgroundColor: "#ffffff",
              overflowX: "auto",
              overflowY: "hidden"
            }}
          >
            <DataTable
              columns={columns}
              data={tableData}
              customStyles={customStyles}
              striped
              highlightOnHover
              noHeader={false}
              fixedHeader
              fixedHeaderScrollHeight="520px"
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10]}
              paginationComponentOptions={{
                rowsPerPageText: "",
                rangeSeparatorText: "of",
                noRowsPerPage: true,
                selectAllRowsItem: false
              }}
              progressPending={historyLoading || dashboardLoading}
              noDataComponent={
                <div style={{ padding: "28px 16px", textAlign: "center", color: "#6b7280" }}>
                  No inventory movement records.
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryMovement;

